import React, { FC } from 'react';
import { Field, Select, TextArea, TextInput, TimeInput } from '@kontentino/ui';
import {
  CTA_DATA,
  CTA_LABEL,
} from '@kontentino/kontentino-constants/GoogleMyBusinessCTA';
import { SUBTYPE } from '@kontentino/kontentino-constants/Posts';
import { Attachments } from 'types/Attachments';
import PostsConfig from 'app/config/posts';
import PostComposerContentText from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentText';
import PostComposerContentMediaAttachments from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentMediaAttachemnts';
import { PostComposerContentProps } from 'app/modules/posts/modules/post/components/composer/PostComposerContent';
import dayjs from 'dayjs';
import DateUtils from 'app/utils/date';
import SingleDatePicker from 'app/components/datepicker-dropdown/SingleDatePicker';
import { useTranslation } from 'react-i18next';

type Props = PostComposerContentProps;

const formatCtaTypeSelectValue = (type: keyof CTA_DATA) => ({
  value: type,
  label: CTA_LABEL[type] || '',
});

const CTA_TYPE_OPTIONS = Object.values(CTA_DATA).map((ctaType) =>
  formatCtaTypeSelectValue(ctaType),
);

const PostComposerContentGoogleMyProfile: FC<Props> = ({
  value,
  onChange,
  onTextEditorInit,
  onTranslationEditorInit,
  swapTextTranslation,
  videoPlayerRef,
}) => {
  const { t } = useTranslation();
  const handleFieldAttachmentValueChange = (newValue: Partial<Attachments>) => {
    onChange((prev) => ({
      attachments: {
        ...prev.attachments,
        ...newValue,
      },
    }));
  };

  const renderTitleField = () => {
    if (
      ([SUBTYPE.GMB_EVENT_POST, SUBTYPE.GMB_OFFER_POST] as number[]).includes(
        value.subtype,
      )
    ) {
      return (
        <Field.Group>
          <Field.Label required>{t('composer.title')}</Field.Label>
          <TextInput
            data-name="post-content_google-my-business_title-field"
            data-cy="post-content_google-my-business_title-field"
            value={value.attachments.title || ''}
            maxLength={PostsConfig.GOOGLE_FORM.MAXIMUM_TITLE_CHARS}
            onChange={(e) =>
              handleFieldAttachmentValueChange({ title: e.target.value })
            }
          />
        </Field.Group>
      );
    }
  };

  const renderEventFields = () => {
    if (SUBTYPE.GMB_EVENT_POST === value.subtype) {
      return (
        <>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Field.Group className="tw-flex-1">
              <Field.Label required>{t('composer.startDate')}</Field.Label>
              <SingleDatePicker
                date={dayjs(value.attachments.startDate)}
                onChange={(date) => {
                  handleFieldAttachmentValueChange({
                    startDate: DateUtils.toDateString(date),
                  });
                }}
                dataCy={{
                  wrapper:
                    'post-content_google-my-business_event-start-date-field',
                }}
              />
            </Field.Group>
            <Field.Group className="tw-flex-1">
              <Field.Label>{t('composer.startTime')}</Field.Label>
              <TimeInput
                data-name="post-content_google-my-business_event-start-time-field"
                data-cy="post-content_google-my-business_event-start-time-field"
                value={value.attachments.startTime || ''}
                onChange={(startTime) =>
                  handleFieldAttachmentValueChange({ startTime })
                }
              />
            </Field.Group>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Field.Group className="tw-flex-1">
              <Field.Label required>{t('composer.endDate')}</Field.Label>
              <SingleDatePicker
                date={dayjs(value.attachments.endDate)}
                onChange={(date) => {
                  handleFieldAttachmentValueChange({
                    endDate: DateUtils.toDateString(date),
                  });
                }}
                dataCy={{
                  wrapper:
                    'post-content_google-my-business_event-end-date-field',
                }}
              />
            </Field.Group>
            <Field.Group className="tw-flex-1">
              <Field.Label>{t('composer.endTime')}</Field.Label>
              <TimeInput
                data-name="post-content_google-my-business_event-end-time-field"
                data-cy="post-content_google-my-business_event-end-time-field"
                value={value.attachments.endTime || ''}
                onChange={(endTime) =>
                  handleFieldAttachmentValueChange({ endTime })
                }
              />
            </Field.Group>
          </div>
        </>
      );
    }
  };

  const renderOfferFields = () => {
    if (SUBTYPE.GMB_OFFER_POST === value.subtype) {
      return (
        <>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Field.Group className="tw-flex-1">
              <Field.Label required>{t('composer.startDate')}</Field.Label>

              <SingleDatePicker
                date={dayjs(value.attachments.startDate)}
                onChange={(date) => {
                  handleFieldAttachmentValueChange({
                    startDate: DateUtils.toDateString(date),
                  });
                }}
                dataCy={{
                  wrapper:
                    'post-content_google-my-business_offer-start-date-field',
                }}
              />
            </Field.Group>
            <Field.Group className="tw-flex-1">
              <Field.Label required>{t('composer.endDate')}</Field.Label>
              <SingleDatePicker
                date={dayjs(value.attachments.endDate)}
                onChange={(date) => {
                  handleFieldAttachmentValueChange({
                    endDate: DateUtils.toDateString(date),
                  });
                }}
                dataCy={{
                  wrapper:
                    'post-content_google-my-business_offer-end-date-field',
                }}
              />
            </Field.Group>
          </div>
          <Field.Group>
            <Field.Label>{t('googleMyProfile.voucherCode')}</Field.Label>
            <TextInput
              data-name="post-content_google-my-business_offer-voucher-code-field"
              data-cy="post-content_google-my-business_offer-voucher-code-field"
              maxLength={PostsConfig.GOOGLE_FORM.MAXIMUM_COUPON_CODE_CHARS}
              value={value.attachments.couponCode || ''}
              onChange={(e) =>
                handleFieldAttachmentValueChange({ couponCode: e.target.value })
              }
            />
          </Field.Group>
          <Field.Group>
            <Field.Label>{t('googleMyProfile.linkToRedeemOffer')}</Field.Label>
            <TextInput
              data-name="post-content_google-my-business_redeem-link-offer-field"
              data-cy="post-content_google-my-business_redeem-link-offer-field"
              value={value.attachments.offerLink || ''}
              onChange={(e) =>
                handleFieldAttachmentValueChange({ offerLink: e.target.value })
              }
            />
          </Field.Group>
          <Field.Group>
            <Field.Label>{t('googleMyProfile.termsAndConditions')}</Field.Label>
            <TextArea
              data-name="post-content_google-my-business_offer-terms-and-conditions-field"
              data-cy="post-content_google-my-business_offer-terms-and-conditions-field"
              rows={3}
              value={value.attachments.termsAndConditions || ''}
              maxLength={
                PostsConfig.GOOGLE_FORM.MAXIMUM_TERMS_AND_CONDITIONS_CHARS
              }
              onChange={(e) =>
                handleFieldAttachmentValueChange({
                  termsAndConditions: e.target.value,
                })
              }
            />
          </Field.Group>
        </>
      );
    }
  };

  const renderCTAFields = () => {
    if (
      ([SUBTYPE.DEFAULT, SUBTYPE.GMB_EVENT_POST] as number[]).includes(
        value.subtype,
      )
    ) {
      return (
        <>
          <Field.Group>
            <Field.Label>{t('googleMyProfile.ctaButton')}</Field.Label>
            <Select
              isClearable
              isSearchable={false}
              options={CTA_TYPE_OPTIONS}
              value={
                value?.attachments.ctaType
                  ? formatCtaTypeSelectValue(
                      value.attachments.ctaType as keyof CTA_DATA,
                    )
                  : null
              }
              onChange={(newValue) =>
                handleFieldAttachmentValueChange({
                  ctaType: newValue?.value || null,
                })
              }
              styles={{
                menuPortal: (styles) => ({
                  ...styles,
                  zIndex: 1010,
                }),
              }}
              menuPortalTarget={document.body}
            />
          </Field.Group>
          {value.attachments.ctaType !== CTA_DATA.CALL && (
            <Field.Group>
              <Field.Label required={value.attachments.ctaType !== null}>
                {t('googleMyProfile.linkToCTA')}
              </Field.Label>
              <TextInput
                data-name="post-content_google-my-business_cta-link-field"
                data-cy="post-content_google-my-business_cta-link-field"
                disabled={!value.attachments.ctaType}
                value={value.attachments.ctaLink || ''}
                onChange={(e) => {
                  handleFieldAttachmentValueChange({ ctaLink: e.target.value });
                }}
              />
            </Field.Group>
          )}
        </>
      );
    }
  };

  return (
    <>
      {renderTitleField()}

      <PostComposerContentText
        value={value}
        onChange={onChange}
        onTextEditorInit={onTextEditorInit}
        onTranslationEditorInit={onTranslationEditorInit}
        swapTextTranslation={swapTextTranslation}
        labels={{
          text: t('composer.summary'),
          translation: t('composer.summaryTranslation'),
        }}
      />

      <PostComposerContentMediaAttachments
        value={value}
        onChange={onChange}
        maxCount={PostsConfig.MIN_FILES_TO_ACCEPT}
        allowedFiles={['image', 'video']}
      />

      {renderEventFields()}
      {renderOfferFields()}
      {renderCTAFields()}
    </>
  );
};

export default PostComposerContentGoogleMyProfile;
